import {
  buildMonthRows,
  categoryOf,
  cellKey,
  chipsForDay,
  chipsInLaneOrder,
  extractTitles,
  isSameCell,
} from "./derive";
import type {
  Chip,
  DayCell,
  FounderMonth,
  ProgressRow,
  StepArtifact,
  TodoTag,
} from "./types";

/** 已过 / 当前 / 未到 / 未开. 未开 is the explicit empty mark, not a guessed skip. */
export type StepMark = "done" | "now" | "later" | "closed";

export type PipelineStep = {
  name: string;
  mark: StepMark;
  detail: string;
  artifact?: StepArtifact;
};

export type PipelineItem = {
  id: string;
  type: TodoTag;
  business: string;
  theme: string;
  /** Short slot such as 周二, taken from the row title. */
  action?: string;
  steps: PipelineStep[];
  note?: string;
  /** Full-width copy for a chip that has no 《题名》 pipeline. */
  body?: string;
};

export type PipelineGroup = {
  type: TodoTag;
  businesses: { name: string; items: PipelineItem[] }[];
};

const TYPE_ORDER: TodoTag[] = ["you", "talk", "ip", "co", "soft"];

const STEP_LABEL: Record<StepMark, string> = {
  done: "已过",
  now: "当前",
  later: "未到",
  closed: "—／未开",
};

export function stepLabel(mark: StepMark) {
  return STEP_LABEL[mark];
}

/**
 * Map one existing progress cell to a mark.
 * Only ✓ / PASS / 已过 / 已发 / 软过 count as done. Empty and 待锁 stay 未开.
 */
export function markFromCell(text: string): StepMark {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t || t === "—" || t === "-" || t === "–") return "closed";
  if (/未发/.test(t)) return "later";
  if (/进行中|打磨/.test(t)) return "now";
  if (/待过|待你|待审|待锁/.test(t)) {
    return /待锁/.test(t) && !/待过|待你|待审/.test(t) ? "closed" : "now";
  }
  if (/✓|PASS|已过|已发|已\s*ship|软过|静帧过/.test(t)) return "done";
  if (/未开|不带|空档/.test(t)) return "closed";
  if (/计划/.test(t)) return "later";
  return "later";
}

function detailText(text: string, mark: StepMark) {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t || t === "—" || t === "-" || t === "–") return "—／未开";
  if (t === "✓") return "已过";
  if (mark === "closed" && /待锁|未开|不带|空档/.test(t)) return "—／未开";
  return t;
}

function businessFromTitle(title: string) {
  return categoryOf(title)?.label ?? title.split("·")[0].trim();
}

function typeFromChip(chip: Chip): TodoTag {
  if (chip.lineKey === "you" || chip.lineKey === "talk") return chip.lineKey;
  if (chip.lineKey === "co") return "co";
  return "ip";
}

function rowForChip(chip: Chip, rows: ProgressRow[]) {
  const titles = extractTitles(chip.title);
  if (!titles.length) return undefined;
  return rows.find((row) =>
    extractTitles(row.subtitle ?? "").some((title) => titles.includes(title)),
  );
}

function progressItem(
  weekLabel: string,
  row: ProgressRow,
  headers: string[],
): PipelineItem {
  const steps: PipelineStep[] = row.span
    ? [
        {
          name: "状态",
          mark: markFromCell(row.span),
          detail: detailText(row.span, markFromCell(row.span)),
        },
      ]
    : (row.cells ?? []).map((cell, index) => {
        const mark = markFromCell(cell);
        const name = headers[index + 1] ?? "步骤";
        const artifact = row.artifacts?.[name];
        return {
          name,
          mark,
          detail: detailText(cell, mark),
          ...(artifact ? { artifact } : {}),
        };
      });
  const slot = row.title.match(/周[一二三四五六日]/)?.[0];
  return {
    id: `pipe:${weekLabel}:${row.title}`,
    type: "ip",
    business: businessFromTitle(row.title),
    theme: row.subtitle || row.span || "—",
    ...(slot ? { action: slot } : {}),
    steps,
    note: row.note || undefined,
  };
}

function looseMark(text: string, weekStatus: string | undefined): StepMark {
  const mark = markFromCell(text);
  if (mark !== "later") return mark;
  if (weekStatus === "now") return "now";
  return "later";
}

function chipOnlyItem(
  chip: Chip,
  cell: DayCell,
  weekStatus: string | undefined,
): PipelineItem {
  const mark = looseMark(chip.status, weekStatus);
  return {
    id: `pipe:${cellKey(cell)}:${chip.id}`,
    type: typeFromChip(chip),
    business: chip.line,
    theme: chip.title,
    steps: [
      {
        name: "状态",
        mark,
        detail: detailText(chip.status, mark),
      },
    ],
    ...(chip.body ? { body: chip.body } : {}),
  };
}

export function buildDayPipeline(
  cell: DayCell,
  month: FounderMonth,
): PipelineGroup[] {
  const week = month.weeks[cell.wk];
  const items = chipsForDay(cell, month).map((chip) => {
    const row = week?.progress
      ? rowForChip(chip, month.progressRows)
      : undefined;
    if (row && week) {
      return {
        ...progressItem(week.label, row, month.progressHeaders),
        id: `pipe:${cellKey(cell)}:${chip.id}`,
        type: typeFromChip(chip),
        business: chip.line,
        theme: chip.title,
      };
    }
    return chipOnlyItem(chip, cell, week?.status);
  });

  return TYPE_ORDER.map((type) => {
    const typed = items.filter((item) => item.type === type);
    const businesses: PipelineGroup["businesses"] = [];
    typed.forEach((item) => {
      const found = businesses.find((group) => group.name === item.business);
      if (found) found.items.push(item);
      else businesses.push({ name: item.business, items: [item] });
    });
    return { type, businesses };
  }).filter((group) => group.businesses.length > 0);
}

/**
 * Day-expand cards in the same top-to-bottom order as that column of
 * `lanesForWeek`. `buildDayPipeline` still fills the copy; it does not
 * decide this order (`TYPE_ORDER` would pull 交流 above the bars).
 */
export function dayExpandItems(
  cell: DayCell,
  month: FounderMonth,
): PipelineItem[] {
  const items = buildDayPipeline(cell, month).flatMap((group) =>
    group.businesses.flatMap((business) => business.items),
  );
  const row = buildMonthRows(month).find((candidate) =>
    candidate.cells.some((day) => isSameCell(day, cell)),
  );
  if (!row) return items;
  const prefix = `pipe:${cellKey(cell)}:`;
  const byChipId = new Map(
    items.map((item) => [item.id.slice(prefix.length), item]),
  );
  return chipsInLaneOrder(cell, row.cells, month).flatMap((chip) => {
    const item = byChipId.get(chip.id);
    return item ? [item] : [];
  });
}

/** Week-phase labels are not a StepMark. */
const BAR_GENERIC_SHORT = new Set([
  "交流",
  "公司事项",
  "默跑",
  "日计划／日报",
  "周计划",
  "周末件前置审",
  "周报",
]);

/**
 * Month-bar completion (v3.4). `status.completed` or a pipeline item whose
 * steps are all `StepMark=done`. A row that only started (选题 ✓, 制作进行中)
 * stays unmarked.
 */
export function isMonthBarDone(
  chip: Chip,
  cell: DayCell,
  month: FounderMonth,
): boolean {
  if (markFromCell(chip.status) === "done") return true;
  if (BAR_GENERIC_SHORT.has(chip.short)) return false;

  const titles = extractTitles(chip.title);
  const needle = titles[0] ?? chip.short;
  if (!needle) return false;

  const related = buildDayPipeline(cell, month)
    .flatMap((group) => group.businesses.flatMap((business) => business.items))
    .filter((item) =>
      titles.length
        ? titles.some((title) => item.theme.includes(title))
        : item.theme.includes(needle),
    );
  return (
    related.length > 0 &&
    related.every(
      (item) =>
        item.steps.length > 0 &&
        item.steps.every((step) => step.mark === "done"),
    )
  );
}
